export const PARTY_CENTRAL_ENTITY_DIMENSIONS = [
    'game',
    'streamer',
    'language',
];
export const PARTY_CENTRAL_GROUP_DIMENSIONS = [
    ...PARTY_CENTRAL_ENTITY_DIMENSIONS,
    'hourUtc',
    'dayOfWeekUtc',
];
export const PARTY_CENTRAL_AXES = [
    'time',
    ...PARTY_CENTRAL_GROUP_DIMENSIONS,
];
/** Display names for axes. User-facing copy must never show the raw ids above. */
export const PARTY_CENTRAL_AXIS_LABELS = {
    time: 'Time',
    game: 'Game',
    streamer: 'Streamer',
    language: 'Language',
    hourUtc: 'Hour',
    dayOfWeekUtc: 'Weekday',
};
export const PARTY_CENTRAL_GRAINS = [
    'capture',
    'hour',
    'day',
    'week',
    'month',
];
export const PARTY_CENTRAL_METRICS = [
    'observationCount',
    'uniqueStreamerCount',
    'uniqueGameCount',
    'uniqueLanguageCount',
    'viewerSum',
    'avgStreamsPerCapture',
    'avgUniqueStreamersPerCapture',
    'avgUniqueGamesPerCapture',
    'avgUniqueLanguagesPerCapture',
    'avgViewers',
    'stickiness',
    'variety',
    'gamePartyScore',
    'streamerPartyScore',
];
export const PARTY_CENTRAL_MEMBER_MODES = [
    'top',
    'latest',
    'selected',
];
export const PARTY_CENTRAL_PAGE_AXES = ['x', 'y'];
export const PARTY_CENTRAL_WEEK_START = 'monday_utc';
export const PARTY_CENTRAL_ERROR_CODES = [
    'invalid_request',
    'invalid_cursor',
    'range_outside_retention',
    'scope_too_large',
];
/**
 * Measures that count distinct entities. Their pipelines first group to one
 * document per (cell, entity) pair, and that stage is held entirely in memory —
 * Atlas caps `$group` at 100 MB and this cluster refuses to spill to disk. The
 * remaining measures are plain sums and stream through at any scope.
 */
export const PARTY_CENTRAL_DISTINCT_MEASURES = [
    'uniqueStreamerCount',
    'uniqueGameCount',
    'uniqueLanguageCount',
    'avgUniqueStreamersPerCapture',
    'avgUniqueGamesPerCapture',
    'avgUniqueLanguagesPerCapture',
    'stickiness',
    'variety',
    'gamePartyScore',
    'streamerPartyScore',
];
/**
 * Published by `/meta` as the explorer's public contract, so every value here
 * must be enforced somewhere. A limit nobody checks describes an explorer we do
 * not have and invites a future change to honour it.
 */
export const PARTY_CENTRAL_LIMITS = {
    /**
     * Descriptive rather than enforced: query bounds come from the actual oldest
     * retained observation, not this figure. It must stay in step with
     * `STREAM_RETENTION_DAYS`, which sets the TTL that does the deleting.
     */
    retentionMonths: 24,
    fineGrainMaxDays: 92,
    maxCompareEntities: 4,
    maxEntityResults: 50,
    maxDrilldownRows: 50,
    maxEntityFilters: 20,
    maxPoolMembers: 100,
    activeWindowSize: 20,
    maxPoolPages: 5,
    maxExplicitMembers: 100,
    maxProjectedCells: 2000,
    /**
     * Streams a distinct-counting measure may scan. Measured against production:
     * 447k matching streams completed in 4.0s, 804k exceeded the `$group` memory
     * cap. Sums have no equivalent ceiling and are not gated.
     */
    maxDistinctMeasureStreams: 500000,
    /**
     * Streams a streamer-grouped view may scan. Streamer is the only
     * high-cardinality dimension — 1.17M streams produce 315k groups — and the
     * aggregation returns every group before ranking, so cost tracks group count
     * rather than the measure. Measured: 48k streams grouped in 2.7s, 249k in
     * 10.3s, 1.17M failed. Pushing the rank into the pipeline would lift this,
     * but `populationCount` and Party Score percentiles come from the whole
     * population, so it would change what they mean: a contract decision, not a
     * refactor.
     */
    maxStreamerAxisStreams: 50000,
};
const partyCentralDistinctMeasures = new Set(PARTY_CENTRAL_DISTINCT_MEASURES);
export const isPartyCentralDistinctMeasure = (measure) => partyCentralDistinctMeasures.has(measure);
/**
 * Streams this view may scan, or null when nothing bounds it. Two independent
 * costs apply, and the tighter one wins: distinct-counting measures hold a
 * document per (cell, entity) pair, and a streamer axis materializes one group
 * per streamer. Plain sums over low-cardinality dimensions are unbounded.
 */
export const getPartyCentralScopeBudget = (request) => {
    const budgets = [];
    if (request.x === 'streamer' || request.y === 'streamer') {
        budgets.push(PARTY_CENTRAL_LIMITS.maxStreamerAxisStreams);
    }
    if (isPartyCentralDistinctMeasure(request.measure)) {
        budgets.push(PARTY_CENTRAL_LIMITS.maxDistinctMeasureStreams);
    }
    return budgets.length > 0 ? Math.min(...budgets) : null;
};
/**
 * Whether this view can be computed over a scope of this size. Both the
 * explorer and the query handler decide from this one function so a view is
 * never offered and then refused.
 */
export const exceedsPartyCentralScopeBudget = (request, observationCount) => {
    const budget = getPartyCentralScopeBudget(request);
    return budget !== null && observationCount > budget;
};
const partyCentralEntityDimensions = new Set(PARTY_CENTRAL_ENTITY_DIMENSIONS);
/**
 * Resolves the fact filters a query will scan. A `selected` entity axis is a
 * filter in all but name, so it narrows the scope — except for the party
 * scores, which rank the whole population before selecting what to display.
 */
export const resolvePartyCentralScope = (request) => {
    const scope = {
        from: request.from,
        to: request.to,
        game: [...request.game],
        streamer: [...request.streamer],
        language: [...request.language],
    };
    const ranksWholePopulation = request.measure === 'gamePartyScore' ||
        request.measure === 'streamerPartyScore';
    if (ranksWholePopulation)
        return scope;
    ['x', 'y'].forEach((position) => {
        const axis = request[position];
        if (!axis || !partyCentralEntityDimensions.has(axis))
            return;
        if (request[`${position}Mode`] !== 'selected')
            return;
        const dimension = axis;
        const ids = request[`${position}Ids`];
        scope[dimension] =
            scope[dimension].length > 0
                ? scope[dimension].filter((id) => ids.includes(id))
                : [...ids];
    });
    return scope;
};
//# sourceMappingURL=partyCentral.js.map